import { motion } from 'framer-motion';
import { springs } from '../lib/animations';

interface AnimatedButtonProps {
  children: React.ReactNode;
  onClick?: () => void;
  variant?: 'primary' | 'secondary' | 'danger';
  size?: 'sm' | 'md' | 'lg';
  disabled?: boolean;
  loading?: boolean;
  className?: string;
  type?: 'button' | 'submit' | 'reset';
}

const variantStyles = {
  primary: {
    base: 'bg-cannabis-700 border-cannabis-500/40 text-cream',
    hover: 'hover:border-cannabis-400/60',
    glow: '0 0 20px rgba(74, 222, 128, 0.25)',
  },
  secondary: {
    base: 'bg-cannabis-800 border-warm/20 text-cream/80',
    hover: 'hover:border-warm/40',
    glow: '0 0 20px rgba(212, 165, 116, 0.2)',
  },
  danger: {
    base: 'bg-red-900/50 border-red-500/30 text-red-200',
    hover: 'hover:border-red-400/50',
    glow: '0 0 20px rgba(239, 68, 68, 0.25)',
  },
};

const sizeStyles = {
  sm: 'px-3 py-1.5 text-xs rounded-lg',
  md: 'px-5 py-2.5 text-sm rounded-xl',
  lg: 'px-7 py-3.5 text-base rounded-xl',
};

export default function AnimatedButton({
  children,
  onClick,
  variant = 'primary',
  size = 'md',
  disabled = false,
  loading = false,
  className = '',
  type = 'button',
}: AnimatedButtonProps) {
  const v = variantStyles[variant];
  const isDisabled = disabled || loading;

  return (
    <motion.button
      type={type}
      onClick={onClick}
      disabled={isDisabled}
      className={`
        font-semibold border transition-colors duration-200
        ${v.base} ${v.hover} ${sizeStyles[size]}
        ${isDisabled ? 'opacity-40 cursor-not-allowed' : 'cursor-pointer'}
        ${className}
      `}
      whileHover={isDisabled ? undefined : { scale: 1.02, boxShadow: v.glow }}
      whileTap={isDisabled ? undefined : { scale: 0.97 }}
      transition={springs.bouncy}
    >
      {loading ? (
        <span className="flex items-center justify-center gap-2">
          <motion.span
            className="inline-block w-4 h-4 border-2 border-current border-t-transparent rounded-full"
            animate={{ rotate: 360 }}
            transition={{ duration: 0.8, repeat: Infinity, ease: 'linear' }}
          />
          {children}
        </span>
      ) : (
        children
      )}
    </motion.button>
  );
}
